import { parseISO, format, eachDayOfInterval } from 'date-fns';
type TherapyData = {
  uuid: string;
  therapyType: string;
  date: string;
  planMinutes: number;
  actualMinutes: number;
  isMissedVisit: boolean;
  hasSplit: any;
};

type DisciplineMap = {
  [key: string]: {
    [key: string]: TherapyData[];
  };
};

export function updateTherapyData(
  dataMap: any,
  date: string,
  discipline: string,
  groupType: string,
  newData: any
): any {
  // Create a deep copy of the dataMap to avoid mutation
  const updatedDataMap = JSON.parse(JSON.stringify(dataMap));

  // Check if the discipline exists
  if (!updatedDataMap[discipline]) {
    console.error(`Discipline ${discipline} not found.`);
    return updatedDataMap; // Return unmodified dataMap
  }

  // Check if the group type exists
  if (!updatedDataMap[discipline][groupType]) {
    console.error(`Group type ${groupType} not found in discipline ${discipline}.`);
    return updatedDataMap; // Return unmodified dataMap
  }

  // Find the entry for the specific date
  const entry = updatedDataMap[discipline][groupType].find((item: any) => item.date === date);

  // If entry is found, update it with new data
  if (entry) {
    Object.assign(entry, newData); // Update the entry without mutating the original state
  } else {
    console.error(`No entry found for date ${date} in ${discipline} - ${groupType}.`);
  }

  return updatedDataMap; // Return the updated copy
}

export const updateDiscipleByMap = (disciple: any) => {
  return disciple === 'PT' ? 'ptMap' : disciple === 'OT' ? 'otMap' : 'slpMap';
};

export const getDisciplineByMap = (map: string): string => {
  switch (map) {
    case 'ptMap':
      return 'PT';
    case 'otMap':
      return 'OT';
    case 'slpMap':
      return 'SLP';
    default:
      throw new Error(`Unknown map: ${map}`);
  }
};

interface TherapyDataOfTotalMin {
  date: string;
  totalPlanMinutes: number;
  totalActualMinutes: number;
}

export function updateTherapyDataByDate(
  data: TherapyDataOfTotalMin[],
  targetDate: string,
  updates: Partial<TherapyDataOfTotalMin>
): TherapyDataOfTotalMin[] {
  // Create a new array to avoid mutating the original data
  return data.map((entry) => {
    // Check if the current entry's date matches the target date
    if (entry.date === targetDate) {
      // Return a new object with updated values
      return { ...entry, ...updates };
    }
    // Return the original entry if the date does not match
    return entry;
  });
}

//summary data
type SummaryData = {
  date: string;
  summary: number;
};

type DisciplineData = {
  [key: string]: SummaryData[];
};

export function replaceDisciplineData(
  data: DisciplineData,
  disciplineType: keyof DisciplineData,
  newData: SummaryData[],
  startDate: string,
  endDate: string
): DisciplineData {
  // Check if the discipline type exists in the data
  if (data[disciplineType]) {
    const existingData = data[disciplineType];

    // Create a map for quick lookup of new data by date
    const newDataMap = new Map<string, number>(newData.map((item) => [item.date, item.summary]));

    // Prepare an array to hold the updated data
    const updatedData: SummaryData[] = [];

    // Create a date range array
    const currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate <= end) {
      const dateString = currentDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      if (newDataMap.has(dateString)) {
        // If the date exists in new data, use the new summary
        updatedData.push({ date: dateString, summary: newDataMap.get(dateString)! });
      } else {
        // If the date is missing in new data, retain the existing summary or set to 0
        const existingItem = existingData.find((item) => item.date === dateString);
        updatedData.push(existingItem ? { ...existingItem } : { date: dateString, summary: 0 });
      }

      // Move to the next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Replace the existing data with the updated data
    data[disciplineType] = updatedData;
  } else {
    console.error(`Discipline type "${disciplineType}" does not exist.`);
  }
  return data;
}

// Function to find specific planning object by discipline and therapyType
interface Planning {
  disciplineId: string;
  discipline: string;
  planMinutes: number;
  therapyType: string;
  actualMinutes: number;
  isMissedVisit: boolean | null;
  hasSplit: any;
}

interface DataEntry {
  uncovered: boolean;
  date: string;
  totalPlanMinutes: number;
  totalActualMinutes: number;
  variation: number;
  planning: Planning[];
}
export function findPlanningByDisciplineAndTherapyType(
  data: DataEntry[],
  discipline: string,
  therapyType: string
): Planning | undefined {
  for (const entry of data) {
    const found = entry.planning.find(
      (p) => p.discipline === discipline && p.therapyType === therapyType
    );
    if (found) {
      return found;
    }
  }
  return undefined; // Return undefined if not found
}

// //summary update

interface TherapyPlanningSummary {
  [key: string]: SummaryData[];
}

export function replaceDataOfSummary(
  currentSummary: TherapyPlanningSummary,
  therapyType: string,
  newData: { [key: string]: SummaryData[] },
  startDate: string,
  endDate: string
): TherapyPlanningSummary {
  const updatedSummary = { ...currentSummary };

  // Generate all dates between startDate and endDate
  const allDatesInRange = eachDayOfInterval({
    start: parseISO(startDate),
    end: parseISO(endDate),
  }).map((date) => format(date, 'yyyy-MM-dd'));

  // Ensure data for all dates
  const existingData = newData?.[therapyType] || [];
  const dataByDate = new Map(existingData.map((item) => [item.date, item.summary]));

  const completeData = allDatesInRange.map((date) => ({
    date,
    summary: dataByDate.get(date) ?? 0, // Use 0 if the date is missing
  }));

  updatedSummary[therapyType] = completeData;

  return updatedSummary;
}



export const getTotalFrequencyData = (freqData: any) => {
  const { OT, SLP, PT, ...restData } = freqData;
  return restData;
};
export function replaceDataByKey(
  originalData: Record<string, any>,
  newData: Record<string, any>,
  keyToReplace: string
): Record<string, any> {
  if (newData.hasOwnProperty(keyToReplace)) {
    return {
      ...originalData,
      [keyToReplace]: newData[keyToReplace],
    };
  } else {
    console.warn(`Key "${keyToReplace}" not found in the new data.`);
    return originalData;
  }
}

export const getFrequencyType = (map: string): string => {
  switch (map) {
    case 'PT':
      return 'freqAlertPT';
    case 'OT':
      return 'freqAlertOT';
    case 'SLP':
      return 'freqAlertSLP';
    default:
      throw new Error(`Unknown map: ${map}`);
  }
};


interface TherapyEntry {
  uuid: string;
  therapyType: string;
  date: string;
  planMinutes: number;
  actualMinutes: number;
  isMissedVisit: boolean;
  hasSplit: any;
}

interface TherapyMap {
  INDIVIDUAL: TherapyEntry[];
  CONCURRENT: TherapyEntry[];
  GROUP: TherapyEntry[];
}

interface Data {
  otMap: TherapyMap;
  ptMap: TherapyMap;
  slpMap: TherapyMap;
}

export function updatePlanMinutes(
  data: Data,
  mapType: 'otMap' | 'ptMap' | 'slpMap',
  therapyType: string,
  date: string,
  newPlanMinutes: any
): Data {
  // Access the correct map based on mapType
  const map = data[mapType];
  if (!map) {
    console.error(`No entries found for mapType: ${mapType}`);
    return data; // Return the original data if mapType is invalid
  }

  // Create a new state object
  const updatedMap: any = { ...map };

  // Update the planMinutes for the specified therapyType and date
  Object.keys(updatedMap).forEach((key: any) => {
    updatedMap[key] = updatedMap[key].map((entry: any) => {
      if (entry.therapyType === therapyType && entry.date === date) {
        return {
          ...entry,
          planMinutes: newPlanMinutes,
        };
      }
      return entry;
    });
  });

  // Return the updated data object
  return {
    ...data,
    [mapType]: updatedMap,
  };
}

export function updateTherapyDataByDateManual(
  data: TherapyDataOfTotalMin[],
  targetDate: string,
  updates: any
): TherapyDataOfTotalMin[] {
  // Create a new array to avoid mutating the original data
  return data.map((entry) => {
    // Check if the current entry's date matches the target date
    if (entry.date === targetDate) {
      // Safely convert both values to numbers and add them
      const updateValue = Number(updates) || 0;
      const totalPlanMinutes = Number(entry.totalPlanMinutes) || 0;

      return {
        ...entry,
        totalPlanMinutes: totalPlanMinutes + updateValue,
      };
    }
    // Return the original entry if the date does not match
    return entry;
  });
}
